import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { debounce } from 'lodash'

/* Bl */
import usePresets from '@/composables/data/usePresets'

import { getRandomString } from '@/helpers/stringHelper'

import { PRESETS_CODES } from '@/config/presetsConfig'

export default function useFavoritesLinksManager() {

	const route = useRoute()

	const {
		loadingState: favoritePresetLoadingState,
		getPresets: getFavorites,
		createPreset: createFavoritePreset,
		patchPreset: patchFavoritePreset,
		// eslint-disable-next-line no-unused-vars
		deletePreset: deleteFavoritePreset,
	} = usePresets(PRESETS_CODES.FAVORITE_LINKS, {}, [])

	const isLoadingFavoritesStatus = ref(false)
	const isLoadedFavoritesStatus = ref(false)
	const isCreatingFavoriteLinkStatus = ref(false)

	const favoritePreset = ref(null)
	const favoritesLinks = ref({
		groups: [],
		ungrouped: [],
	})

	const getCompressedLink = (link) => {
		return [
			link.key,
			link.title,
			link.subtitle,
			link.route,
		]
	}
	const getDecompressedLink = (link) => {
		return {
			key: link[0],
			title: link[1],
			subtitle: link[2],
			route: link[3],
		}
	}

	const getCompressedFavoritesLinks = () => {
		const compressedFavoritesLinks = {
			groups: [],
			ungrouped: [],
		}
		favoritesLinks.value.groups.forEach((group) => {
			compressedFavoritesLinks.groups.push([
				group.key,
				group.name,
				group.links.map((link) => {
					return getCompressedLink(link)
				}),
			])
		})
		compressedFavoritesLinks.ungrouped = favoritesLinks.value.ungrouped.map((link) => {
			return getCompressedLink(link)
		})
		return compressedFavoritesLinks
	}
	const getDecompressedFavoritesLinks = (config) => {
		const decompressedFavoritesLinks = {
			groups: [],
			ungrouped: [],
		}
		config.groups.forEach((group) => {
			decompressedFavoritesLinks.groups.push({
				key: group[0],
				name: group[1],
				links: group[2].map((link) => {
					return getDecompressedLink(link)
				}),
				expanded: false,
			})
		})
		decompressedFavoritesLinks.ungrouped = config.ungrouped.map((link) => {
			return getDecompressedLink(link)
		})
		return decompressedFavoritesLinks
	}

	const getFavoritePreset = () => {
		return getFavorites()
	}

	const getFirstOrCreateFavoritesPreset = () => {
		return new Promise((resolve, reject) => {
			isLoadingFavoritesStatus.value = true
			getFavoritePreset()
				.then((res) => {
					if (Array.isArray(res) && res.length) {
						favoritePreset.value = res[0]
						favoritesLinks.value = getDecompressedFavoritesLinks(res[0].config)
						isLoadedFavoritesStatus.value = true
						isLoadingFavoritesStatus.value = false
						resolve(true)
					} else {
						createFavoritePreset({
							name: 'Избранные ссылки',
							config: {
								groups: [],
								ungrouped: [],
							},
						})
							.then((res) => {
								favoritePreset.value = res
								favoritesLinks.value = getDecompressedFavoritesLinks(res.config)
								isLoadedFavoritesStatus.value = true
								isLoadingFavoritesStatus.value = false
								resolve(true)
							})
					}
				})
				.catch((err) => {
					reject(err)
				})
		})
	}

	const syncFavoritesPreset = () => {
		return new Promise((resolve, reject) => {
			// console.log('syncFavoritesPreset')
			const config = getCompressedFavoritesLinks()
			isLoadingFavoritesStatus.value = true
			patchFavoritePreset({
				presetId: favoritePreset.value.id,
				payload: {
					config,
				}
			})
				.then(() => {
					isLoadingFavoritesStatus.value = false
					resolve(true)
				})
				.catch((err) => {
					reject(err)
				})
		})
	}

	const createFavoriteGroup = (payload) => {
		return new Promise((resolve) => {
			const group = {
				key: getRandomString(10),
				name: payload.name,
				links: [],
				expanded: false,
			}
			favoritesLinks.value.groups.push(group)
			syncFavoritesPreset()
			resolve(group)
		})
	}

	const changeFavoriteGroupName = (groupKey, name) => {
		return new Promise((resolve) => {
			const group = favoritesLinks.value.groups.find((group) => group.key === groupKey)
			if (group) {
				group.name = name
			}
			syncFavoritesPreset()
				.then(() => {
					resolve(true)
				})
		})
	}

	const deleteFavoriteGroup = (groupKey) => {
		return new Promise((resolve) => {
			const group = favoritesLinks.value.groups.find((group) => group.key === groupKey)
			if (group && group.links.length) {
				favoritesLinks.value.ungrouped.push(...group.links)
			}
			favoritesLinks.value.groups = favoritesLinks.value.groups.filter((group) => group.key !== groupKey)
			syncFavoritesPreset()
			resolve(true)
		})
	}

	const getRoute = () => {
		function getRouteQuery(query) {
			const routeQuery = {...query}
			delete routeQuery.openedEditingForm
			delete routeQuery.openedPreview
			return routeQuery
		}
		return {
			name: route.name,
			params: route.params,
			query: getRouteQuery(route.query),
		}
	}

	const createFavoriteLink = (groupKey, payload) => {
		return new Promise((resolve, reject) => {
			isCreatingFavoriteLinkStatus.value = true
			const route = getRoute()
			const link = {
				key: getRandomString(10),
				title: payload.title,
				subtitle: payload.subtitle,
				route,
			}
			if (groupKey) {
				const group = favoritesLinks.value.groups.find((group) => group.key === groupKey)
				if (group) {
					group.links.push(link)
				} else {
					favoritesLinks.value.ungrouped.push(link)
				}
			} else {
				favoritesLinks.value.ungrouped.push(link)
			}
			syncFavoritesPreset()
				.then(() => {
					isCreatingFavoriteLinkStatus.value = false
					resolve(link)
				})
				.catch((err) => {
					isCreatingFavoriteLinkStatus.value = false
					reject(err)
				})
		})
	}

	const changeFavoriteLink = (groupKey, linkKey, { title, subtitle }) => {
		return new Promise((resolve) => {
			const link = groupKey
				? favoritesLinks.value.groups.find((group) => group.key === groupKey).links.find((link) => link.key === linkKey)
				: favoritesLinks.value.ungrouped.find((link) => link.key === linkKey)
			if (link) {
				link.title = title
				link.subtitle = subtitle
			}
			syncFavoritesPreset()
				.then(() => {
					resolve(true)
				})
		})
	}

	const deleteFavoriteLink = (groupKey, linkKey) => {
		return new Promise((resolve) => {
			if (groupKey) {
				const group = favoritesLinks.value.groups.find((group) => group.key === groupKey)
				if (group) {
					group.links = group.links.filter((link) => link.key !== linkKey)
				} else {
					favoritesLinks.value.ungrouped = favoritesLinks.value.ungrouped.filter((link) => link.key !== linkKey)
				}
			} else {
				favoritesLinks.value.ungrouped = favoritesLinks.value.ungrouped.filter((link) => link.key !== linkKey)
			}
			syncFavoritesPreset()
				.then(() => {
					resolve(true)
				})
		})
	}

	const onChangeOrdering = debounce(() => {
		return syncFavoritesPreset()
	}, 100)


	return {
		favoritePresetLoadingState,

		isLoadingFavoritesStatus,
		isLoadedFavoritesStatus,
		isCreatingFavoriteLinkStatus,

		favoritesLinks,
		getFavoritePreset,
		getFirstOrCreateFavoritesPreset,

		createFavoriteGroup,
		changeFavoriteGroupName,
		deleteFavoriteGroup,

		createFavoriteLink,
		changeFavoriteLink,
		deleteFavoriteLink,

		onChangeOrdering,
	}
}
